import { Button, makeStyles, Tooltip } from '@material-ui/core';
import IconPeople from '@material-ui/icons/People';
import classNames from 'classnames';
import { differenceInDays, intlFormatDistance } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';

import {
  startOfDayInBoulderTimezone,
  startOfTodayInBoulderTimezone,
} from 'src/businessHours/timezone';
import type { PatientHighlightsQuery } from 'src/patientHeader/components/badges/queries/patientHighlights.gql';
import { useSWRPatientHighlights } from 'src/patientHeader/components/badges/queries/useSWRPatientHighlights';
import { useButtonStyles } from 'src/patientHeader/components/badges/styles/useButtonStyles';
import { useTooltipStyles } from 'src/patientHeader/components/badges/styles/useTooltipStyles';

const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const LastCompletedVisitDate: React.FC<{
  patientId: string;
}> = ({ patientId }) => {
  const { data } = useSWRPatientHighlights(patientId);
  const tooltipStyles = useTooltipStyles();
  const buttonStyles = useButtonStyles();
  const styles = useStyles();

  if (!data) return null;

  const { link, text, tooltip } = getLastVisitDetails(patientId, data);

  return (
    <Tooltip
      title={tooltip}
      placement="right"
      enterDelay={300}
      enterNextDelay={300}
      leaveTouchDelay={300}
      classes={{
        tooltip: tooltipStyles.tooltip,
      }}
    >
      <a href={link}>
        <Button
          classes={{ startIcon: styles.startIcon }}
          className={classNames([buttonStyles.button, buttonStyles.neutral])}
          fullWidth
          size="small"
          startIcon={<IconPeople data-testid="last-completed-visit-date-icon" />}
          variant="outlined"
        >
          {text}
        </Button>
      </a>
    </Tooltip>
  );
};

const getLastVisitDetails = (
  patientId: string,
  data: PatientHighlightsQuery,
): {
  link?: string;
  text: string;
  tooltip: string;
} => {
  const lastCompletedVisit = data?.patientHighlights?.lastCompletedVisit;
  if (!lastCompletedVisit) return { text: 'None', tooltip: 'None' };

  const { date, id, providers } = lastCompletedVisit;
  const lastCompletedVisitDate = date ? startOfDayInBoulderTimezone(date) : null;
  const today = startOfTodayInBoulderTimezone();

  let daysSinceLastVisit: number | null = null;
  let timeDistance: string | null = null;

  if (lastCompletedVisitDate) {
    daysSinceLastVisit = differenceInDays(lastCompletedVisitDate, today);

    timeDistance =
      Math.abs(daysSinceLastVisit) >= 1
        ? intlFormatDistance(lastCompletedVisitDate, today, { unit: 'day' })
        : 'today';
  }

  return {
    link: id ? `/patients/${patientId}/activity#activity-${id}` : undefined,
    text: lastCompletedVisitDate
      ? formatInTimeZone(lastCompletedVisitDate, localZone, 'MM/dd/yyyy')
      : 'None',
    tooltip: timeDistance
      ? `Last clinical visit: ${timeDistance}${
          providers?.length ? `, with ${providers.join(', ')}` : ''
        }`
      : 'None',
  };
};

const useStyles = makeStyles({
  startIcon: {
    marginRight: 4,
  },
});
